import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Lock, Pencil, Plus, Power, Trash2, Users } from 'lucide-react';
import { PageContainer } from '../../../shared/components/PageContainer';
import { usePerfis, useAtualizarPerfil, useExcluirPerfil } from '../hooks';
import { PerfilFormModal } from '../components/PerfilFormModal';
import { MatrizPermissoes } from '../components/MatrizPermissoes';
import type { Role } from '../api';

/** Erro 409 do backend traz a contagem de usuários que bloqueiam a operação. */
interface BloqueioResponse {
  message?: string;
  activeUserCount?: number;
  roleId?: string;
}

function extrairErro(err: unknown): BloqueioResponse {
  const data = (err as { response?: { data?: BloqueioResponse | { message?: string[] } } }).response?.data;
  if (!data) return { message: 'Não foi possível concluir a operação.' };
  const message = (data as { message?: string | string[] }).message;
  return {
    ...(data as BloqueioResponse),
    message: Array.isArray(message) ? message.join(', ') : message,
  };
}

const PerfisPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: perfis = [], isLoading, isError } = usePerfis();
  const atualizar = useAtualizarPerfil();
  const excluir = useExcluirPerfil();

  const [selecionadoId, setSelecionadoId] = useState<string | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<Role | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [bloqueio, setBloqueio] = useState<(BloqueioResponse & { perfil: Role }) | null>(null);
  const [matrizSuja, setMatrizSuja] = useState(false);

  const selecionado = perfis.find((p) => p.id === selecionadoId) ?? perfis[0] ?? null;

  useEffect(() => {
    if (!selecionadoId && perfis.length > 0) setSelecionadoId(perfis[0].id);
  }, [perfis, selecionadoId]);

  /** Avisa antes de fechar a aba com alterações pendentes na grade. */
  useEffect(() => {
    if (!matrizSuja) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [matrizSuja]);

  function mostrarSucesso(mensagem: string) {
    setSucesso(mensagem);
    setTimeout(() => setSucesso(null), 5000);
  }

  function selecionar(perfil: Role) {
    if (matrizSuja && !window.confirm('Há alterações não salvas na matriz. Descartar?')) return;
    setMatrizSuja(false);
    setSelecionadoId(perfil.id);
  }

  async function alternarAtivo(perfil: Role) {
    setBloqueio(null);
    try {
      await atualizar.mutateAsync({ id: perfil.id, payload: { isActive: !perfil.isActive } });
      mostrarSucesso(`Perfil "${perfil.name}" ${perfil.isActive ? 'desativado' : 'reativado'}.`);
    } catch (err) {
      setBloqueio({ ...extrairErro(err), perfil });
    }
  }

  async function remover(perfil: Role) {
    setBloqueio(null);
    if (!window.confirm(`Excluir o perfil "${perfil.name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await excluir.mutateAsync(perfil.id);
      if (selecionadoId === perfil.id) setSelecionadoId(null);
      mostrarSucesso(`Perfil "${perfil.name}" excluído.`);
    } catch (err) {
      setBloqueio({ ...extrairErro(err), perfil });
    }
  }

  return (
    <PageContainer
      title="Perfis e permissões"
      subtitle="Defina o que cada perfil pode visualizar, criar, editar e deletar em cada funcionalidade."
      breadcrumbs={[{ label: 'Administração', to: '/admin' }, { label: 'Perfis' }]}
      action={
        <button
          type="button"
          onClick={() => { setEmEdicao(null); setModalAberto(true); }}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" /> Novo perfil
        </button>
      }
    >
      {sucesso && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-800">
          <CheckCircle2 className="h-4 w-4" /> {sucesso}
        </div>
      )}

      {bloqueio && (
        <div className="mb-4 rounded-lg bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">{bloqueio.message}</p>
              {bloqueio.activeUserCount ? (
                <button
                  type="button"
                  onClick={() => navigate(`/usuarios?role=${bloqueio.perfil.slug}`)}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  <Users className="h-3.5 w-3.5" />
                  Ver os {bloqueio.activeUserCount} usuário(s) com este perfil
                </button>
              ) : null}
            </div>
            <button type="button" onClick={() => setBloqueio(null)} className="text-amber-700 hover:text-amber-900">
              ×
            </button>
          </div>
        </div>
      )}

      {isError && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
          Não foi possível carregar os perfis.
        </div>
      )}

      <div className="grid gap-4 lg:h-[calc(100vh-13rem)] lg:min-h-[32rem] lg:grid-cols-[340px_1fr]">
        <aside className="flex min-h-0 flex-col rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
            {isLoading ? 'Carregando…' : `${perfis.length} perfis`}
          </div>
          <ul className="max-h-96 min-h-0 flex-1 divide-y divide-gray-100 overflow-auto lg:max-h-none">
            {perfis.map((perfil) => {
              const ativo = selecionado?.id === perfil.id;
              return (
                <li key={perfil.id}>
                  <div
                    className={`w-full px-4 py-3 ${ativo ? 'bg-blue-50 border-l-4 border-blue-600' : 'border-l-4 border-transparent hover:bg-gray-50'}`}
                  >
                    <button type="button" onClick={() => selecionar(perfil)} className="w-full text-left">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${perfil.isActive ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                          {perfil.name}
                        </span>
                        {perfil.isSystem && <Lock className="h-3.5 w-3.5 text-amber-600" />}
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {perfil.activeUserCount} usuário(s) ativo(s) · {perfil.permissionCount} funcionalidade(s)
                      </p>
                    </button>

                    <div className="mt-2 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => { setEmEdicao(perfil); setModalAberto(true); }}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                        title="Editar nome e descrição"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => alternarAtivo(perfil)}
                        disabled={perfil.isSystem}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30"
                        title={
                          perfil.isSystem
                            ? 'Perfil de sistema não pode ser desativado'
                            : perfil.isActive
                              ? 'Desativar perfil'
                              : 'Reativar perfil'
                        }
                      >
                        <Power className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remover(perfil)}
                        disabled={perfil.isSystem}
                        className="rounded p-1.5 text-gray-500 hover:bg-red-100 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-30"
                        title={
                          perfil.isSystem
                            ? 'Perfil de sistema não pode ser excluído'
                            : 'Excluir perfil'
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      {perfil.isSystem && (
                        <span className="ml-1 text-[11px] text-amber-700">perfil de sistema</span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
          {selecionado ? (
            <MatrizPermissoes
              key={selecionado.id}
              perfil={selecionado}
              onSaved={mostrarSucesso}
              onDirtyChange={setMatrizSuja}
            />
          ) : (
            <div className="py-20 text-center text-gray-500">
              {isLoading ? 'Carregando perfis…' : 'Selecione um perfil para editar as permissões.'}
            </div>
          )}
        </section>
      </div>

      <PerfilFormModal
        open={modalAberto}
        perfil={emEdicao}
        onClose={() => setModalAberto(false)}
        onSaved={mostrarSucesso}
      />
    </PageContainer>
  );
};

export default PerfisPage;
